// src/services/payrollService.js
import { tenantDataService } from './tenantDataService.js';
import { isAdvancedPlansEnabled, buildPayoutsForPeriod } from './compPlanService';

export class PayrollService {
  static async generatePayslips() {
    try {
      console.log('🔄 PayrollService: Generating payslips...');
      
      // Check if advanced compensation plans are enabled
      const useAdvancedPlans = await isAdvancedPlansEnabled();
      
      if (useAdvancedPlans) {
        return await this.generateAdvancedPayslips();
      } else {
        return await this.generateRoleBasedPayslips();
      }
    } catch (error) {
      console.error('❌ PayrollService.generatePayslips failed:', error);
      throw new Error(`Failed to generate payslips: ${error.message}`);
    }
  }

  static async generateAdvancedPayslips() {
    try {
      console.log('🔄 PayrollService: Generating advanced compensation payslips...');
      
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();
      
      // Build payouts for current month using compensation plans
      const payouts = await buildPayoutsForPeriod(currentYear, currentMonth + 1);
      
      // Convert payouts to payslip format
      const payslips = payouts.map(payout => ({
        id: `payslip-${payout.staffId}-${currentYear}-${currentMonth + 1}`,
        staffId: payout.staffId,
        staffName: payout.staffName,
        staffEmail: payout.staffEmail,
        role: payout.role,
        month: currentMonth + 1,
        year: currentYear,
        appointmentCount: payout.appointmentCount,
        totalEarnings: payout.totalPayout,
        compensationBreakdown: payout.breakdown,
        generatedAt: new Date().toISOString(),
        status: 'pending_review',
        paymentMethod: 'advanced_plans'
      }));

      console.log('✅ PayrollService: Generated', payslips.length, 'advanced payslips');
      return payslips;
      
    } catch (error) {
      console.error('❌ PayrollService.generateAdvancedPayslips failed:', error);
      throw new Error(`Failed to generate advanced payslips: ${error.message}`);
    }
  }

  static async generateRoleBasedPayslips() {
    try {
      console.log('🔄 PayrollService: Generating role-based payslips...');
      
      const [appointments, staff, settings] = await Promise.all([
        tenantDataService.getAll('appointments'),
        tenantDataService.getAll('staff'),
        tenantDataService.getAll('settings')
      ]);

      const clinicSettings = settings.length > 0 ? settings[0] : {};
    
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    
      // Filter appointments for current month that are attended
      const monthlyAppointments = appointments.filter(apt => {
        const aptDate = new Date(apt.date);
        return aptDate.getMonth() === currentMonth && 
               aptDate.getFullYear() === currentYear &&
               apt.status === 'attended' &&
               apt.staffId;
      });

      // Group appointments by staff
      const staffAppointments = {};
      monthlyAppointments.forEach(apt => {
        if (!staffAppointments[apt.staffId]) {
          staffAppointments[apt.staffId] = [];
        }
        staffAppointments[apt.staffId].push(apt);
      });

      // Generate payslips
      const payslips = [];
      
      Object.keys(staffAppointments).forEach(staffId => {
        const staffMember = staff.find(s => s.id === staffId);
        if (!staffMember) return;

        const appointments = staffAppointments[staffId];
        let totalEarnings = 0;
        const appointmentDetails = [];

        appointments.forEach(apt => {
          // Try both treatmentRates (AdminSettings format) and treatmentTypes (SettingsService format)
          let treatmentRate = clinicSettings.treatmentRates?.[apt.type];
          
          if (!treatmentRate && clinicSettings.treatmentTypes) {
            // Fallback to treatmentTypes array format
            const treatmentType = clinicSettings.treatmentTypes.find(t => t.name === apt.type);
            if (treatmentType) {
              treatmentRate = {
                type: treatmentType.pricingType,
                value: treatmentType.value
              };
            }
          }
          
          if (!treatmentRate) return;

          let appointmentValue = 0;
          if (treatmentRate.type === 'hourly') {
            appointmentValue = (apt.duration / 60) * treatmentRate.value;
          } else {
            appointmentValue = treatmentRate.value;
          }

          // Apply role rate percentage
          const roleRate = clinicSettings.roleRates?.[staffMember.role] || 50;
          const staffEarning = (appointmentValue * roleRate) / 100;
          
          totalEarnings += staffEarning;
          appointmentDetails.push({
            date: apt.date,
            time: apt.time,
            client: apt.client?.name || 'Unknown Client',
            type: apt.type,
            duration: apt.duration,
            rate: treatmentRate.value,
            rolePercentage: roleRate,
            earning: staffEarning
          });
        });

        payslips.push({
          id: `payslip-${staffId}-${currentYear}-${currentMonth + 1}`,
          staffId: staffId,
          staffName: staffMember.name,
          staffEmail: staffMember.email,
          role: staffMember.role,
          month: currentMonth + 1,
          year: currentYear,
          appointmentCount: appointments.length,
          totalEarnings: totalEarnings,
          appointmentDetails: appointmentDetails,
          generatedAt: new Date().toISOString(),
          status: 'pending_review',
          paymentMethod: 'role_based'
        });
      });

      console.log('✅ PayrollService: Generated', payslips.length, 'role-based payslips');
      return payslips;
      
    } catch (error) {
      console.error('❌ PayrollService.generateRoleBasedPayslips failed:', error);
      throw new Error(`Failed to generate role-based payslips: ${error.message}`);
    }
  }

  static async savePayslips(payslips) {
    try {
      console.log('🔄 PayrollService: Saving', payslips.length, 'payslips...');
      
      const savedPayslips = [];
      for (const payslip of payslips) {
        const saved = await tenantDataService.create('payslips', payslip);
        savedPayslips.push(saved);
      }
      
      console.log('✅ PayrollService: Saved payslips successfully');
      return savedPayslips;
    } catch (error) {
      console.error('❌ PayrollService.savePayslips failed:', error);
      throw new Error(`Failed to save payslips: ${error.message}`);
    }
  }

  static async approvePayslips(payslipIds) {
    try {
      console.log('🔄 PayrollService: Approving payslips:', payslipIds);
      
      const updatedPayslips = [];
      for (const payslipId of payslipIds) {
        const updated = await tenantDataService.update('payslips', payslipId, {
          status: 'approved',
          approvedAt: new Date().toISOString()
        });
        updatedPayslips.push(updated);
      }
      
      console.log('✅ PayrollService: Approved', updatedPayslips.length, 'payslips');
      return updatedPayslips;
    } catch (error) {
      console.error('❌ PayrollService.approvePayslips failed:', error);
      throw new Error(`Failed to approve payslips: ${error.message}`);
    }
  }

  static async releasePayslips(payslipIds) {
    try {
      console.log('🔄 PayrollService: Releasing payslips:', payslipIds);
      
      const updatedPayslips = [];
      for (const payslipId of payslipIds) {
        const updated = await tenantDataService.update('payslips', payslipId, {
          status: 'released',
          releasedAt: new Date().toISOString()
        });
        updatedPayslips.push(updated);
      }
      
      // TODO: In production, integrate with email service to notify staff
      console.log('✅ PayrollService: Released payslips to staff:', payslipIds);
      
      return updatedPayslips;
    } catch (error) {
      console.error('❌ PayrollService.releasePayslips failed:', error);
      throw new Error(`Failed to release payslips: ${error.message}`);
    }
  }

  static async getPayslipsForStaff(staffId) {
    try {
      console.log('🔄 PayrollService: Getting payslips for staff:', staffId);
      
      const payslips = await tenantDataService.query('payslips', [
        ['staffId', '==', staffId],
        ['status', '==', 'released']
      ]);
      
      console.log('✅ PayrollService: Found', payslips.length, 'released payslips for staff');
      return payslips;
    } catch (error) {
      console.error('❌ PayrollService.getPayslipsForStaff failed:', error);
      throw new Error(`Failed to get payslips for staff: ${error.message}`);
    }
  }

  static async getPendingPayslips() {
    try {
      console.log('🔄 PayrollService: Getting pending payslips...');
      
      const payslips = await tenantDataService.query('payslips', [
        ['status', '==', 'pending_review']
      ]);
      
      console.log('✅ PayrollService: Found', payslips.length, 'pending payslips');
      return payslips;
    } catch (error) {
      console.error('❌ PayrollService.getPendingPayslips failed:', error);
      throw new Error(`Failed to get pending payslips: ${error.message}`);
    }
  }

  static shouldRunPayroll() {
    const now = new Date();
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const isLastDay = now.getDate() === lastDayOfMonth.getDate();
    const isNearMidnight = now.getHours() === 23 && now.getMinutes() >= 59;
    
    return isLastDay && isNearMidnight;
  }

  static async checkAndRunAutomaticPayroll() {
    try {
      if (this.shouldRunPayroll()) {
        console.log('🔄 PayrollService: Running automatic payroll...');
        
        const payslips = await this.generatePayslips();
        const savedPayslips = await this.savePayslips(payslips);
        
        // Create admin notification
        const notification = {
          id: Date.now().toString(),
          type: 'payroll_ready',
          title: 'Payroll Ready for Review',
          message: `${payslips.length} payslips have been generated and are ready for your review.`,
          createdAt: new Date().toISOString(),
          read: false
        };
        
        await tenantDataService.create('admin_notifications', notification);
        
        // Dispatch event for UI updates
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('payroll-generated', { detail: savedPayslips }));
        }
        
        console.log('✅ PayrollService: Automatic payroll completed');
        return savedPayslips;
      }
      return null;
    } catch (error) {
      console.error('❌ PayrollService.checkAndRunAutomaticPayroll failed:', error);
      throw new Error(`Failed to run automatic payroll: ${error.message}`);
    }
  }
}

// Initialize payroll checking
export function initializePayrollService() {
  // Check every minute if it's time to run payroll
  setInterval(() => {
    PayrollService.checkAndRunAutomaticPayroll();
  }, 60000); // Check every minute
}
